import type { FC, ReactNode } from 'react'

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import {
    Mic,
    MicOff,
    RotateCcw,
    Wind
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { heartBurst, originOf } from '@/lib/confetti'
import { WISH } from '@/constants/birthday'

const CANDLE_COUNT = 5
const LISTEN_LIMIT_MS = 20000

const SPRINKLES = [
    { left: '12%', top: '38%', tint: 'bg-gold' },
    { left: '24%', top: '62%', tint: 'bg-white' },
    { left: '37%', top: '44%', tint: 'bg-rose-200' },
    { left: '52%', top: '66%', tint: 'bg-gold' },
    { left: '64%', top: '40%', tint: 'bg-white' },
    { left: '77%', top: '60%', tint: 'bg-rose-200' },
    { left: '88%', top: '42%', tint: 'bg-gold' }
]

type MicSession = {
    stream: MediaStream
    context: AudioContext
    frame: number
    timeout: number
}

// Cake with candles she can blow out by tapping, all at once, or by blowing into the mic.
const WishCake: FC = (): ReactNode => {
    const [lit, setLit] = useState<boolean[]>(() => Array.from({ length: CANDLE_COUNT }, () => true))
    const [listening, setListening] = useState(false)
    const [micFailed, setMicFailed] = useState(false)
    const micRef = useRef<MicSession | null>(null)
    const cakeRef = useRef<HTMLDivElement>(null)
    const allOut = lit.every((value) => !value)

    const stopMic = useCallback((): void => {
        const session = micRef.current

        if (!session) return

        micRef.current = null
        cancelAnimationFrame(session.frame)
        window.clearTimeout(session.timeout)
        session.stream.getTracks().forEach((track) => track.stop())
        void session.context.close().catch(() => undefined)
        setListening(false)
    }, [])

    useEffect(() => stopMic, [stopMic])

    useEffect(() => {
        if (!allOut) return

        stopMic()
        void heartBurst(originOf(cakeRef.current))
    }, [allOut, stopMic])

    const blowOne = useCallback((): void => {
        setLit((current) => {
            const index = current.findIndex(Boolean)

            if (index === -1) return current

            return current.map((value, candle) => candle === index ? false : value)
        })
    }, [])

    const blowCandle = (index: number): void => {
        setLit((current) => current.map((value, candle) => candle === index ? false : value))
    }

    const listen = async (): Promise<void> => {
        if (listening) {
            stopMic()
            return
        }

        setMicFailed(false)

        const AudioContextClass = window.AudioContext
            ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

        if (!navigator.mediaDevices?.getUserMedia || !AudioContextClass) {
            setMicFailed(true)
            return
        }

        const context = new AudioContextClass()

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
            })

            if (context.state === 'suspended') await context.resume()

            const analyser = context.createAnalyser()

            analyser.fftSize = 1024
            context.createMediaStreamSource(stream).connect(analyser)

            const samples = new Uint8Array(analyser.fftSize)
            let loudFrames = 0
            let cooldown = 0

            const tick = (): void => {
                const session = micRef.current

                if (!session) return

                analyser.getByteTimeDomainData(samples)

                let sum = 0

                for (const sample of samples) {
                    const centered = (sample - 128) / 128
                    sum += centered * centered
                }

                const level = Math.sqrt(sum / samples.length)

                if (cooldown > 0) cooldown -= 1

                loudFrames = level > 0.16 ? loudFrames + 1 : 0

                if (loudFrames > 4 && cooldown === 0) {
                    blowOne()
                    loudFrames = 0
                    cooldown = 12
                }

                session.frame = requestAnimationFrame(tick)
            }

            micRef.current = {
                stream,
                context,
                frame: 0,
                timeout: window.setTimeout(stopMic, LISTEN_LIMIT_MS)
            }

            setListening(true)
            micRef.current.frame = requestAnimationFrame(tick)
        }
        catch {
            void context.close().catch(() => undefined)
            setMicFailed(true)
        }
    }

    const relight = (): void => {
        setLit(Array.from({ length: CANDLE_COUNT }, () => true))
    }

    return (
        <div className='flex h-full flex-col rounded-[2rem] border border-primary/10 bg-white p-6 text-center shadow-xl shadow-primary/5 sm:p-8'>
            <h3 className='font-heading text-3xl font-semibold'>
                {WISH.title}
            </h3>

            <p className='mt-3 text-muted-foreground'>
                {WISH.text}
            </p>

            <div ref={cakeRef} className='relative mx-auto mt-14 h-60 w-72 max-w-full'>
                <div className='absolute inset-x-0 top-2 z-10 flex justify-center gap-5'>
                    {lit.map((on, index) => (
                        <button
                            key={index}
                            type='button'
                            onClick={() => blowCandle(index)}
                            disabled={!on}
                            aria-label={`${WISH.candle} ${index + 1}`}
                            className='relative h-16 w-3.5 rounded-t-sm bg-[repeating-linear-gradient(-45deg,var(--color-primary)_0_5px,white_5px_10px)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-default'
                        >
                            <span aria-hidden className='absolute -top-1.5 left-1/2 h-2 w-0.5 -translate-x-1/2 rounded-full bg-wine/70' />

                            {on ? (
                                <span
                                    aria-hidden
                                    className='absolute -top-8 left-1/2 h-7 w-4 -translate-x-1/2 origin-bottom animate-flicker rounded-[50%_50%_45%_45%/60%_60%_40%_40%] bg-gradient-to-t from-orange-500 via-amber-300 to-yellow-50 shadow-[0_0_18px_6px] shadow-amber-300/60 motion-reduce:animate-none'
                                />
                            ) : (
                                <span
                                    aria-hidden
                                    className='absolute -top-6 left-1/2 size-3 -translate-x-1/2 animate-smoke rounded-full bg-zinc-300/70 motion-reduce:hidden'
                                />
                            )}
                        </button>
                    ))}
                </div>

                <div className='absolute bottom-[5.75rem] left-1/2 z-[1] h-20 w-48 -translate-x-1/2 overflow-hidden rounded-t-3xl rounded-b-md bg-rose-200 shadow-inner'>
                    <div className='absolute inset-x-0 top-0 h-5 bg-white' />
                    <div className='absolute left-5 top-4 h-5 w-4 rounded-b-full bg-white' />
                    <div className='absolute left-16 top-4 h-7 w-4 rounded-b-full bg-white' />
                    <div className='absolute right-14 top-4 h-4 w-4 rounded-b-full bg-white' />
                    <div className='absolute right-5 top-4 h-6 w-4 rounded-b-full bg-white' />
                </div>

                <div className='absolute bottom-3 left-1/2 h-[5.25rem] w-64 max-w-[calc(100%-0.5rem)] -translate-x-1/2 overflow-hidden rounded-t-3xl rounded-b-md bg-primary shadow-lg shadow-primary/25'>
                    <div className='absolute inset-x-0 top-0 h-4 bg-rose-100' />
                    <div className='absolute left-8 top-3 h-5 w-4 rounded-b-full bg-rose-100' />
                    <div className='absolute left-24 top-3 h-7 w-4 rounded-b-full bg-rose-100' />
                    <div className='absolute right-20 top-3 h-4 w-4 rounded-b-full bg-rose-100' />
                    <div className='absolute right-8 top-3 h-6 w-4 rounded-b-full bg-rose-100' />

                    {SPRINKLES.map((sprinkle) => (
                        <span
                            key={`${sprinkle.left}-${sprinkle.top}`}
                            aria-hidden
                            className={cn('absolute h-1 w-2.5 rotate-45 rounded-full', sprinkle.tint)}
                            style={{ left: sprinkle.left, top: sprinkle.top }}
                        />
                    ))}
                </div>

                <div className='absolute bottom-0 left-1/2 h-4 w-72 max-w-full -translate-x-1/2 rounded-full bg-muted shadow-md' />
            </div>

            <div aria-live='polite' className='mt-6 min-h-8'>
                {allOut && (
                    <p className='animate-in fade-in zoom-in-95 duration-500 font-script text-4xl text-primary motion-reduce:animate-none'>
                        {WISH.done}
                    </p>
                )}

                {!allOut && listening && (
                    <p className='text-sm font-bold text-primary'>
                        {WISH.listening}
                    </p>
                )}

                {!allOut && micFailed && (
                    <p className='text-sm font-semibold text-muted-foreground'>
                        {WISH.micError}
                    </p>
                )}
            </div>

            <div className='mt-auto flex flex-wrap justify-center gap-3 pt-6'>
                {allOut ? (
                    <button
                        type='button'
                        onClick={relight}
                        className='inline-flex items-center gap-2 rounded-full border border-primary/25 px-6 py-3 text-sm font-bold text-primary transition hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30'
                    >
                        <RotateCcw className='size-4' />
                        {WISH.relight}
                    </button>
                ) : (
                    <>
                        <button
                            type='button'
                            onClick={() => void listen()}
                            aria-pressed={listening}
                            className={cn(
                                'inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30',
                                listening ? 'bg-wine text-white' : 'border border-primary/25 text-primary hover:bg-primary/5'
                            )}
                        >
                            {listening ? <MicOff className='size-4' /> : <Mic className='size-4' />}
                            {WISH.mic}
                        </button>

                        <button
                            type='button'
                            onClick={() => setLit(lit.map(() => false))}
                            className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30'
                        >
                            <Wind className='size-4' />
                            {WISH.all}
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

export default WishCake
